import React, { useEffect, useReducer } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { saveAs as saveAsFile } from 'file-saver';
import { useTranslation } from 'react-i18next';

import { useCreateTrainingMatrixFileGenerationProcessMutation } from 'components/hooks/trainingMatrixFileGenerationProcessMutations';
import { useTrainingMatrixFileGenerationProcessQuery } from 'components/hooks/trainingMatrixFileGenerationProcessQueries';
import useWindowStorage from 'components/hooks/useWindowStorage';

import FilledButton from 'components/application/buttons/FilledButton';
import CircleQuestion from 'components/application/CircleQuestion';
import Tooltip from 'components/application/Tooltip';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import Filters from 'components/trainingRegisterReports/components/Filters';
import ReportGeneratorModal from 'components/trainingRegisterReports/components/ReportGeneratorModal';
import {
  assignFiltersParams,
  buildFormValues,
  buildLocalStorageItem,
  validateForm,
} from 'components/trainingRegisterReports/helpers';
import useReportForm, {
  initialEstimatesState,
  initialFiltersState,
} from 'components/trainingRegisterReports/hooks/useReportForm';
import ValidationErrorsMessages from 'components/validationErrors/components/ValidationErrorsMessages';
import validationErrorsReducer, {
  initialValidationErrorsState,
  mapValidationErrorsDispatch,
} from 'components/validationErrors/reducers/validationErrorsReducer';

import { asyncScrollToTop } from 'components/helpers/scrolling';

const additionalValidators = [];
const initialFormValues = {
  estimates: initialEstimatesState,
  filters: initialFiltersState,
};

const defaultFilters = [
  'personnelTypeFilter',
  'courseFilter',
  'courseRequirementFilter',
];

export default function TrainingMatrixReport() {
  const currentActor = useCurrentActor();
  const queryClient = useQueryClient();
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.reports.TrainingMatrixReport',
  });
  const { t: i18nTCommon } = useTranslation('common');
  const { t: i18nTHelpers } = useTranslation('components', {
    keyPrefix: 'trainingRegisterReports.helpers',
  });
  const [getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem] =
    useWindowStorage(
      `trainingMatrixReport|${currentActor.user.id}|${currentActor.division.id}`,
    );
  const {
    formValues,
    handleFilterAddition,
    handleFilterChange,
    handleFilterDeselection,
    handleFilterRemoval,
    handleFilterSelection,
  } = useReportForm('trainingMatrix', initialFormValues, (initialState) =>
    buildFormValues({
      type: 'trainingMatrix',
      formValues: initialState,
      additionalValidators,
      getLocalStorageItem,
      removeLocalStorageItem,
    }),
  );
  const [validationErrors, validationErrorsDispatch] = useReducer(
    validationErrorsReducer,
    initialValidationErrorsState,
  );
  const validationErrorsHandlers = mapValidationErrorsDispatch(
    validationErrorsDispatch,
  );

  const createMutation = useCreateTrainingMatrixFileGenerationProcessMutation();
  const generationProcessId = createMutation.data?.data.id;

  const { data: domainFileGenerationProcess, isError: isPollingError } =
    useTrainingMatrixFileGenerationProcessQuery({ generationProcessId });

  const progress = (() => {
    if (createMutation.isIdle) return 'initial';
    if (createMutation.isError || isPollingError) return 'failed';
    if (!domainFileGenerationProcess?.data) return 'generating';
    const { status, url } = domainFileGenerationProcess.data.attributes;
    if (status === 'failed') return 'failed';
    if (status === 'queued' || status === 'working') return 'generating';
    if (status === 'completed') {
      if (url) {
        return 'downloadReady';
      } else {
        return 'noResults';
      }
    }
  })();

  const validationErrorsKeys = Object.keys(validationErrors);
  const isReportGenerationDisabled = validationErrorsKeys.some(
    (key) => validationErrors[key].fieldHighlighted,
  );

  // remove initial navigation options
  useEffect(() => {
    window.history.replaceState(null, '', window.location.pathname);
  }, []);

  const ensureValidForm = () =>
    validateForm({
      formValues,
      additionalValidators,
      safe: true,
      translations: i18nTHelpers('validateForm', { returnObjects: true }),
    });

  const resetReportGenerationProcess = () => {
    createMutation.reset();
    if (generationProcessId) {
      queryClient.removeQueries({
        queryKey: [
          'trainingMatrix',
          'fileGenerationProcesses',
          generationProcessId,
        ],
      });
    }
  };

  const startReportGenerationProcess = () => {
    const errors = ensureValidForm();
    validationErrorsHandlers.handleValidationErrorsReset();
    if (Object.keys(errors).length !== 0) {
      asyncScrollToTop().then(() =>
        validationErrorsHandlers.handleValidationErrorsAssignment(errors),
      );
    } else {
      resetReportGenerationProcess();
      const params = {};
      assignFiltersParams({
        params,
        filters: formValues.filters,
        currentDivision: currentActor.division,
      });
      createMutation.mutate({ generationProcessParams: params });
      setLocalStorageItem(
        buildLocalStorageItem({ type: 'trainingMatrix', formValues }),
      );
    }
  };

  const handleDownloadReadyClick = () => {
    const url = domainFileGenerationProcess?.data.attributes.url;
    if (url) {
      saveAsFile(url);
      resetReportGenerationProcess();
    }
  };

  return (
    <>
      <div className='tw-rounded-t-md tw-border-1 tw-border-b-0 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-10'>
        {validationErrorsKeys.length !== 0 && (
          <div className='tw-mb-10'>
            <ValidationErrorsMessages validationErrors={validationErrors} />
          </div>
        )}
        <div className='tw-mb-10 tw-flex tw-items-center'>
          <h2 className='tw-m-0 tw-mr-2 tw-text-xl tw-font-semibold tw-tracking-tight'>
            {i18nT('title')}
          </h2>
          <Tooltip placement='top' tooltip={i18nT('tooltip')} trigger='hover'>
            <CircleQuestion />
          </Tooltip>
        </div>
        <div className='tw-max-w-[460px]'>
          <Filters
            defaultFilters={defaultFilters}
            filters={formValues.filters}
            onAddFilter={handleFilterAddition}
            onChange={handleFilterChange}
            onDeselect={handleFilterDeselection}
            onFocus={validationErrorsHandlers.handleValidationErrorsUnhighlight}
            onRemoveFilter={(filter) => {
              handleFilterRemoval(filter);
              validationErrorsHandlers.handleValidationErrorsUnhighlight(
                filter,
              );
            }}
            onSelect={handleFilterSelection}
            validationErrors={validationErrors}
          />
        </div>
      </div>
      <div className='tw-flex tw-justify-end tw-rounded-b-md tw-rounded-t-none tw-border-1 tw-border-solid tw-border-grey-100 tw-bg-white tw-p-5'>
        <FilledButton
          color='mint'
          disabled={isReportGenerationDisabled}
          onClick={startReportGenerationProcess}
        >
          {i18nTCommon('shared.generateReport')}
        </FilledButton>
      </div>
      {progress !== 'initial' && (
        <ReportGeneratorModal
          isOpen={true}
          onCancelClick={resetReportGenerationProcess}
          onDownloadReadyClick={handleDownloadReadyClick}
          onTryAgainClick={startReportGenerationProcess}
          progress={progress}
          type='trainingMatrix'
        />
      )}
    </>
  );
}
